// Portal Layout Component
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { 
  LayoutDashboard, 
  Users, 
  Users2,
  FileText, 
  Calendar, 
  Settings,
  Upload,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Shield,
  KeyRound,
  Loader2,
  Bell,
  Phone,
  Building2,
  Map,
  Fingerprint,
  ScrollText,
  Eye,
  Search,
  BarChart3,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import portalApi from '../lib/api';
import { toast } from 'sonner';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const navItems = [
  { path: '/portal', label: 'Dashboard', icon: LayoutDashboard, permission: null },
  { path: '/portal/cases', label: 'Cases', icon: FileText, permission: null, hideInStandard: true },
  { path: '/portal/renewals', label: 'Renewals', icon: RefreshCw, permission: null, hideInStandard: true },
  { path: '/portal/clients', label: 'Clients', icon: User, permission: null, hideInPartner: true },
  { path: '/portal/reports', label: 'Reports', icon: BarChart3, roles: ['admin', 'ls_admin', 'program_manager'] },
  { path: '/portal/producers', label: 'Producers', icon: Users, permission: 'producers.manage' },
  { path: '/portal/users', label: 'Users', icon: Users, anyPermission: ['users.manage', 'users.deactivate'] },
  { path: '/portal/workspaces', label: 'Workspaces', icon: Building2, permission: 'workspaces.manage' },
  { path: '/portal/coverage-years', label: 'Coverage Years', icon: Calendar, permission: 'years.manage' },
  { path: '/portal/import', label: 'Bulk Import', icon: Upload, permission: 'bulk.import' },
  { path: '/portal/audit-logs', label: 'Audit Log', icon: ScrollText, permission: 'workspaces.manage' },
  { path: '/portal/access-requests', label: 'Access Requests', icon: KeyRound, permission: 'workspaces.manage' },
  { path: '/portal/team', label: 'My Team', icon: Users2, permission: 'workspaces.manage' },
  { path: '/portal/trash', label: 'Trash', icon: Trash2, roles: ['admin'] },
  { path: '/portal/oscar', label: 'Oscar Payments', icon: Phone, roles: ['admin'] },
];

export function PortalLayout({ children }) {
  const { user, logout, hasPermission, activeWorkspace, workspaces, setActiveWorkspace,
          isImpersonating, impersonationName, exitViewAs, viewAsUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'admin';

  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [viewAsUsers, setViewAsUsers] = useState([]);
  const [viewAsSearch, setViewAsSearch] = useState('');
  const [viewAsLoading, setViewAsLoading] = useState(false);
  const [viewAsBusy, setViewAsBusy] = useState(false);

  const openViewAs = async () => {
    setViewAsOpen(true);
    setViewAsSearch('');
    setViewAsLoading(true);
    try {
      const list = await portalApi.getUsers();
      setViewAsUsers((list || []).filter((u) => u.role !== 'admin'));
    } catch (err) {
      toast.error(err.message || 'Could not load users');
    } finally {
      setViewAsLoading(false);
    }
  };

  const handleViewAs = async (u) => {
    setViewAsBusy(true);
    try {
      await viewAsUser(u.id);
    } catch (err) {
      toast.error(err.message || 'Could not start View as');
      setViewAsBusy(false);
    }
  };

  const switchWorkspace = () => {
    setActiveWorkspace(null);
    navigate('/portal');
  };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  const loadNotifs = async () => {
    try {
      const data = await portalApi.getNotifications();
      setNotifs(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  const openNotif = async (n) => {
    setNotifOpen(false);
    if (!n.read) { try { await portalApi.markNotificationRead(n.id); } catch {} }
    navigate(n.link || '/portal');
    loadNotifs();
  };

  const markAll = async () => {
    try { await portalApi.markAllNotificationsRead(); loadNotifs(); } catch {}
  };

  const handleChangePassword = async () => {
    if (pwForm.next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setPwSaving(true);
    try {
      await portalApi.changePassword(pwForm.current, pwForm.next);
      toast.success('Password updated');
      setPwOpen(false);
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPwSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login');
  };

  const { showWarning, stayActive } = useIdleTimeout(handleLogout);

  const isStandard = activeWorkspace?.client_profile === 'full';
  const myRoles = new Set([user?.role, ...Object.values(user?.workspace_roles || {}).flat()]);
  const filteredNavItems = navItems.filter(item =>
    (!item.permission || hasPermission(item.permission)) &&
    (!item.anyPermission || item.anyPermission.some(p => hasPermission(p))) &&
    (!item.roles || item.roles.some(r => myRoles.has(r))) &&
    !(item.hideInStandard && isStandard) &&
    !(item.hideInPartner && !isStandard)
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Read-only "View as" banner */}
      {isImpersonating && (
        <div className="sticky top-0 z-[60] bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-center gap-4 text-sm font-medium shadow" data-testid="impersonation-banner">
          <span className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Viewing as <strong>{impersonationName}</strong> — read-only. Changes are disabled.
          </span>
          <Button size="sm" variant="outline" className="h-7 bg-white/80 hover:bg-white border-amber-700 text-amber-950" onClick={exitViewAs} data-testid="exit-view-as-btn">
            Exit view
          </Button>
        </div>
      )}
      {/* Mobile header */}
      <div className="lg:hidden bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-sky-400" />
          <span className="font-semibold">Calyvra Workspace</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setNotifOpen(!notifOpen)} className="relative p-2 rounded-full hover:bg-slate-800" data-testid="notif-bell-mobile">
            <Bell className="h-5 w-5 text-white" />
            {unread > 0 && <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{unread}</span>}
          </button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-slate-800"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>
      {notifOpen && (
        <div className="lg:hidden fixed top-14 right-2 left-2 z-50 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-800">Notifications</span>
            {unread > 0 && <button onClick={markAll} className="text-xs text-sky-600">Mark all read</button>}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifs.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No notifications</p>}
            {notifs.map((n) => (
              <button key={n.id} onClick={() => openNotif(n)} className={`w-full text-left px-4 py-3 border-b border-slate-50 ${!n.read ? 'bg-sky-50/50' : ''}`}>
                <p className="text-sm text-slate-700">{n.message}</p>
                <p className="text-xs text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 flex flex-col transform transition-transform duration-200 ease-in-out
          lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-800 flex-shrink-0">
            <Shield className="h-7 w-7 text-sky-400 flex-shrink-0" />
            <div>
              <h1 className="text-white font-semibold text-sm">Calyvra Workspace</h1>
              <p className="text-slate-400 text-xs">Insurance &amp; Premium support</p>
              <a
                href="tel:+13054323557"
                className="mt-1 inline-flex items-center gap-1 text-sky-400 hover:text-sky-300 text-xs font-semibold transition-colors"
                data-testid="sidebar-calyvra-phone"
                title="Calyvra Insurance client support line — share this with clients"
              >
                <Phone className="h-3 w-3" /> (305) 432-3557
              </a>
              <p className="text-[10px] text-slate-500 leading-tight">Calyvra line for clients</p>
            </div>
          </div>

          {/* Active workspace + switch */}
          {activeWorkspace && (
            <div className="px-4 py-3 border-b border-slate-800 flex-shrink-0" data-testid="active-workspace-banner">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Current Workspace</p>
              <div className="flex items-center justify-between gap-2 mt-1">
                <span className="text-sky-300 font-semibold text-sm truncate" data-testid="active-workspace-name">{activeWorkspace.name}</span>
                {workspaces.length > 1 && (
                  <button onClick={switchWorkspace} className="text-xs text-slate-400 hover:text-white whitespace-nowrap" data-testid="switch-workspace-btn">Switch</button>
                )}
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                (item.path !== '/portal' && location.pathname.startsWith(item.path));
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors
                    ${isActive 
                      ? 'bg-sky-500/20 text-sky-400' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-800 flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left hover:bg-slate-800 transition-colors">
                  <div className="h-8 w-8 rounded-full bg-sky-500/20 flex items-center justify-center">
                    <User className="h-4 w-4 text-sky-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                    <p className="text-xs text-slate-400 truncate capitalize">{user?.role?.replace('_', ' ')}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{user?.email}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPwOpen(true)} className="flex items-center gap-2" data-testid="change-password-menu-item">
                  <KeyRound className="h-4 w-4" />
                  <span>Change password</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/portal/security')} className="flex items-center gap-2" data-testid="security-menu-item">
                  <Fingerprint className="h-4 w-4" />
                  <span>Security &amp; passkeys</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-red-600">
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 min-h-screen lg:ml-0">
          <div className="hidden lg:flex items-center justify-end gap-3 px-8 pt-6">
            {isSuperAdmin && !isImpersonating && (
              <Button variant="outline" size="sm" onClick={openViewAs} data-testid="view-as-btn" className="gap-2">
                <Eye className="h-4 w-4" /> View as user
              </Button>
            )}
            <div className="relative">
              <button onClick={() => setNotifOpen(!notifOpen)} className="relative p-2 rounded-full hover:bg-slate-100" data-testid="notif-bell">
                <Bell className="h-5 w-5 text-slate-600" />
                {unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center" data-testid="notif-count">{unread}</span>}
              </button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                  <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden" data-testid="notif-dropdown">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
                      <span className="text-sm font-semibold text-slate-800">Notifications</span>
                      {unread > 0 && <button onClick={markAll} className="text-xs text-sky-600 hover:underline">Mark all read</button>}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifs.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No notifications</p>}
                      {notifs.map((n) => (
                        <button key={n.id} onClick={() => openNotif(n)} className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 ${!n.read ? 'bg-sky-50/50' : ''}`} data-testid="notif-item">
                          <p className="text-sm text-slate-700">{n.message}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="p-6 lg:p-8 lg:pt-4">
            {children}
          </div>
        </main>
      </div>

      <Dialog open={viewAsOpen} onOpenChange={(o) => { if (!viewAsBusy) setViewAsOpen(o); }}>
        <DialogContent data-testid="view-as-dialog">
          <DialogHeader><DialogTitle>View the portal as another user</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">
            See exactly what a user sees, in read-only mode. You can't create, edit, delete or export while viewing.
          </p>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name or email…"
              value={viewAsSearch}
              onChange={(e) => setViewAsSearch(e.target.value)}
              className="pl-9"
              data-testid="view-as-search"
            />
          </div>
          <div className="max-h-80 overflow-y-auto -mx-1 px-1">
            {viewAsLoading && <p className="text-sm text-slate-400 text-center py-6">Loading users…</p>}
            {!viewAsLoading && viewAsUsers
              .filter((u) => {
                const q = viewAsSearch.trim().toLowerCase();
                if (!q) return true;
                return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
              })
              .map((u) => (
                <button
                  key={u.id}
                  onClick={() => handleViewAs(u)}
                  disabled={viewAsBusy}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-slate-100 disabled:opacity-50"
                  data-testid={`view-as-user-${u.id}`}
                >
                  <div className="h-8 w-8 rounded-full bg-sky-500/15 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-sky-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                    <p className="text-xs text-slate-400 truncate">{u.email} · {String(u.role || '').replace('_', ' ')}</p>
                  </div>
                </button>
              ))}
            {!viewAsLoading && viewAsUsers.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-6">No users available.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} data-testid="current-password-input" />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} data-testid="new-password-input" />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="confirm-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
            <Button onClick={handleChangePassword} disabled={pwSaving} data-testid="save-password-btn">
              {pwSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showWarning} onOpenChange={(o) => { if (!o) stayActive(); }}>
        <DialogContent data-testid="idle-warning-dialog">
          <DialogHeader><DialogTitle>Are you still there?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            For your security, you'll be signed out automatically in about 1 minute due to inactivity.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={handleLogout} data-testid="idle-signout-btn">Sign out now</Button>
            <Button onClick={stayActive} className="bg-sky-500 hover:bg-sky-600" data-testid="idle-stay-btn">Stay signed in</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PortalLayout;
